import React, { useEffect } from "react"
import { trns } from "../helper.js";
import { Form, Heading, Segment } from "ui-components";
import { Button } from "ui-components";
import { clsx } from "clsx";
import { FormGroup } from "ui-components";
import { ControlLabel } from "ui-components";
import { Item } from "ui-components";

type Props = {
  validateCode: (code: string) => Promise<boolean>;
  resend: () => Promise<void>;
  onSuccess?: () => Promise<void>;
}

const ValidationCode = ({ validateCode, resend, onSuccess }: Props) => {
  const [code, setCode] = React.useState<string>("");
  const [digit1, setDigit1] = React.useState<string>("");
  const [digit2, setDigit2] = React.useState<string>("");
  const [digit3, setDigit3] = React.useState<string>("");
  const [digit4, setDigit4] = React.useState<string>("");
  const [resendDisabled, setResendDisabled] = React.useState(false);
  const [countdown, setCountdown] = React.useState(0);
  const [isPasting, setIsPasting] = React.useState(false);
  const [validationCodeSuccess, setValidationCodeSuccess] = React.useState(false);

  const digit1Ref = React.useRef<HTMLInputElement>(null);
  const digit2Ref = React.useRef<HTMLInputElement>(null);
  const digit3Ref = React.useRef<HTMLInputElement>(null);
  const digit4Ref = React.useRef<HTMLInputElement>(null);

  const valueIsDigit = (value: string) => {
    return /^\d$/.test(value);
  }

  useEffect(() => {
    if (digit1Ref.current) {
      digit1Ref.current.focus();
    }
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    setCode(digit1 + digit2 + digit3 + digit4);
  }, [digit1, digit2, digit3, digit4]);

  useEffect(() => {
    if (isPasting) {
      if (digit4Ref.current) {
        digit4Ref.current.focus();
      }
      setIsPasting(false);
    }
  }, [isPasting]);

  useEffect(() => {
    // track copy/paste to allow splitting input across fields
    const handlePaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;
      const pasteData = e.clipboardData.getData("text") || "";
      // filter out pasteData to the first 4 digits only, ignore any non-digit characters
      const digitsOnly = pasteData.replace(/\D/g, "").slice(0, 4);
      if (digitsOnly.length === 4) {
        e.preventDefault();
        setDigit1(digitsOnly[0]);
        setDigit2(digitsOnly[1]);
        setDigit3(digitsOnly[2]);
        setDigit4(digitsOnly[3]);
        setIsPasting(true);
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  useEffect(() => {
    // Go back to the previous input if you hit backspace on an empty field
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Backspace") {
        const target = e.target as HTMLInputElement;
        if (target.value === "") {
          if (target === digit4Ref.current && digit3Ref.current) {
            digit3Ref.current.focus();
          } else if (target === digit3Ref.current && digit2Ref.current) {
            digit2Ref.current.focus();
          } else if (target === digit2Ref.current && digit1Ref.current) {
            digit1Ref.current.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (validationCodeSuccess) {
      //Countdown interval to show timer
      setCountdown(5);
      const countDownClock = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countDownClock);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      //Proceed with post-success actions after 5 seconds to allow the user to see the success message and countdown
      const timer = setTimeout(async () => {
        onSuccess && await onSuccess();
      }, 5000);

      return () => {
        clearTimeout(timer);
        clearInterval(countDownClock);
      };
    }
  }, [validationCodeSuccess, onSuccess]);

  const handleResend = async () => {
    try {
      await resend();
    } catch {
      return;
    }
    setResendDisabled(true);
    setCountdown(30);
  };

  return (
    <Form className="fieldset">
      {!validationCodeSuccess && (
        <>
          <Heading headingLevel="h4">
            {trns("IDS_REGISTRATION_VERIFICATION_CODE_HEADER", "Email Verification")}
          </Heading>

          <Segment className="verification-code">
            <Item className="verification-instructions">
              {trns(
                "IDS_REGISTRATION_VERIFICATION_CODE_INSTRUCTIONS",
                "Thank you for registering, an email has been sent to your address with a verification code. Please either click on the verification link or enter the code below to verify your email."
              )}
            </Item>

            <Segment flex verticalCenter className="full-width">
              <FormGroup>
                <ControlLabel>
                  {trns(
                    "IDS_REGISTRATION_VERIFICATION_CODE_LABEL",
                    "Verification Code:"
                  )}
                </ControlLabel>
                <div className={clsx({ "form-element": true })}>
                  <div
                    className={clsx({
                      "form-control-container": true,
                      "verification-code-inputs": true,
                    })}
                  >
                    <input
                      className="form-control verification-code-input"
                      onInput={(e: React.FormEvent<HTMLInputElement>) => {
                        if (valueIsDigit(e.currentTarget.value)) {
                          setDigit1(e.currentTarget.value)
                          if (digit2Ref.current) {
                            digit2Ref.current.focus();
                          }
                        } else if (e.currentTarget.value === "") {
                          setDigit1("");
                        }
                      }}
                      value={digit1}
                      type="text"
                      ref={digit1Ref}
                      maxLength={1}
                    />
                    <input
                      className="form-control verification-code-input"
                      onInput={(e: React.FormEvent<HTMLInputElement>) => {
                        if (valueIsDigit(e.currentTarget.value)) {
                          setDigit2(e.currentTarget.value)
                          if (digit3Ref.current) {
                            digit3Ref.current.focus();
                          }
                        } else if (e.currentTarget.value === "") {
                          setDigit2("");
                        }
                      }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.currentTarget.value.length === 0 && digit1Ref.current && digit1Ref.current.value === "") {
                          if (digit1Ref.current) {
                            digit1Ref.current.focus();
                          }
                        }
                      }}
                      value={digit2}
                      type="text"
                      ref={digit2Ref}
                      maxLength={1}
                    />
                    <input
                      className="form-control verification-code-input"
                      onInput={(e: React.FormEvent<HTMLInputElement>) => {
                        if (valueIsDigit(e.currentTarget.value)) {
                          setDigit3(e.currentTarget.value)
                          if (digit4Ref.current) {
                            digit4Ref.current.focus();
                          }
                        } else if (e.currentTarget.value === "") {
                          setDigit3("");
                        }
                      }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.currentTarget.value.length === 0 && digit2Ref.current && digit2Ref.current.value === "") {
                          if (digit2Ref.current) {
                            digit2Ref.current.focus();
                          }
                        }
                      }}
                      value={digit3}
                      type="text"
                      ref={digit3Ref}
                      maxLength={1}
                    />
                    <input
                      className="form-control verification-code-input"
                      onInput={(e: React.FormEvent<HTMLInputElement>) => {
                        if (valueIsDigit(e.currentTarget.value)) {
                          setDigit4(e.currentTarget.value)
                        } else if (e.currentTarget.value === "") {
                          setDigit4("");
                        }
                      }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.currentTarget.value.length === 0 && digit3Ref.current && digit3Ref.current.value === "") {
                          if (digit3Ref.current) {
                            digit3Ref.current.focus();
                          }
                        }
                      }}
                      value={digit4}
                      type="text"
                      ref={digit4Ref}
                      maxLength={1}
                    />
                  </div>
                </div>

              </FormGroup>
              <FormGroup className="verification-buttons-group">
                <Button
                  type="button"
                  disabled={code.length < 4}
                  onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    const success = await validateCode(code)
                    if (success) {
                      setValidationCodeSuccess(true);
                    }
                  }}
                  className={clsx({
                    "common-btn": true,
                    "verify-button": true,
                  })}
                >
                  {trns("IDS_REGISTRATION_VERIFY_EMAIL", "Verify Email")}
                </Button>
                <Button
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    handleResend()
                  }}
                  disabled={resendDisabled}
                  className={clsx({
                    "common-btn": true,
                    "resend-button": true,
                  })}
                >
                  {resendDisabled
                    ? `${trns("IDS_RESEND_EMAIL", "Resend Email")} (${countdown})`
                    : trns("IDS_RESEND_EMAIL", "Resend Email")}
                </Button>
              </FormGroup>
            </Segment>

          </Segment>
        </>
      )}

      {validationCodeSuccess && (
        <>
          <Heading headingLevel="h4">
            {trns("IDS_REGISTRATION_VERIFICATION_CODE_SUCCESS", "Email Verified Successfully!")}
          </Heading>

          <Segment className="verification-code">
            <Item className="verification-instructions">
              {trns(
                "IDS_REGISTRATION_VERIFICATION_CODE_SUCCESS_INSTRUCTIONS",
                "Your email has been verified. You will be redirected to the login page in "
              )}
              {countdown > 0 && `${countdown}`}
              {trns(
                "IDS_REGISTRATION_VERIFICATION_CODE_SUCCESS_INSTRUCTIONS_SECONDS",
                " seconds.")}
            </Item>
            <FormGroup className="verification-button-success">
              <Button
                type="button"
                onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault()
                  onSuccess && await onSuccess();
                }}
                className={clsx({
                  "common-btn": true,
                  "proceed-button": true,
                })}
              >
                {trns("IDS_REGISTRATION_GO_TO_LOGIN", "Go to Login")}
              </Button>
            </FormGroup>
          </Segment>
        </>
      )}
    </Form>
  )
}

export default ValidationCode;